import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import dynamic from 'next/dynamic';

import ZpButton from 'components/zpButton';
import { formatSubPageText } from 'utils';
import { CLUSTER_HEADER_CATEGORY, CPP_SUBPAGES } from 'utils/constant';

import { toggleUserFavouriteCompany } from '../../../services/userService';
import {
  updateCompanyIsSaved,
  updateUserFavouriteCompanies,
} from '../../../store/companyPage/actions';
import { openLoginModal, updateAddCompanyToFavouriteInQueue } from '../../../store/modals/actions';
import { CoverImage } from '../common/CoverImage';
import SearchBar from '../common/SearchBar';
import SubHeader from '../common/SubHeader';

import CompanyClusterHeaderTitle from './CompanyClusterHeaderTitle';

import subHeaderCalloutClasses from '../common/subHeaderCallout.module.css';

const PendingChanges = dynamic(() => import('./PendingChangesHeader'));

const getTitle = (company, page, subTitle, data) => {
  const titles = {
    [CPP_SUBPAGES.OVERVIEW]: `Working at ${company}`,
    [CPP_SUBPAGES.SALARY]: `${company} salaries`,
    [CPP_SUBPAGES.DEMOGRAPHICS]: `${company} demographics and statistics`,
    [CPP_SUBPAGES.JOBS]: `${company} jobs`,
    [CPP_SUBPAGES.HISTORY]: `${company} history`,
    [CPP_SUBPAGES.REVIEWS]: `${company} reviews`,
    [CPP_SUBPAGES.LOCATIONS]: `${company} locations`,
    [CPP_SUBPAGES.EXECUTIVES]: `${company} ${subTitle}`,
    [CPP_SUBPAGES.COMPETITORS]: `${company} competitors`,
    [CPP_SUBPAGES.REVENUE]: `${company} revenue`,
    [CPP_SUBPAGES.JOB_TITLE_COMBO_JOBS]: data?.metadata?.H1,
    [CPP_SUBPAGES.LOCATION_COMBO_JOBS]: `${company} ${data?.criteria?.city}, ${data?.criteria?.stateAbbr} Jobs`,
    [CPP_SUBPAGES.JOB_TITLE_SALARY]: `${company} ${data?.criteria?.title?.toLowerCase?.()} salary`,
    [CPP_SUBPAGES.STATE_SALARY]: `${company} salaries in ${data.criteria?.state}`,
    [CPP_SUBPAGES.CITY_SALARY]: `${company} salaries in ${data.criteria?.city}, ${data.criteria?.stateAbbr}`,
    [CPP_SUBPAGES.FAQ]: data?.metadata?.H1,
  };

  return titles[page] || `${company} ${subTitle}`;
};

const CompanyClusterHeader = ({
  subPageName,
  subTitle,
  updateUserFavouriteCompanies,
  updateCompanyIsSaved,
  updateAddCompanyToFavouriteInQueue,
  openLoginModal,
  favouriteCompanies,
  companyIsSaved,
  user,
  dynamicPage,
  ...data
}) => {
  const {
    claimedCompany,
    pendingChanges,
    companyInfo,
    userFavouriteCompanies: userFavouriteCompaniesInit = [],
    canClaimCompany,
    desktopDevice,
  } = data;
  const { companyName, companyNameCPS } = companyInfo;
  const isUserLoggedIn = !!(user && user.isLoggedIn);

  const [isFavouriteLoading, setFavouriteLoading] = useState(false);

  useEffect(() => {
    updateUserFavouriteCompanies(userFavouriteCompaniesInit);
    const companyIsSavedInit = userFavouriteCompaniesInit.some(
      (item) => item.companyID === companyInfo.companyID
    );
    updateCompanyIsSaved(companyIsSavedInit);
  }, []);

  const toggleSaveCompany = () => {
    if (isUserLoggedIn) {
      setFavouriteLoading(true);
      updateCompanyIsSaved(!companyIsSaved);
      toggleUserFavouriteCompany(companyInfo, favouriteCompanies)
        .then((companies) => {
          updateUserFavouriteCompanies(companies);
        })
        .finally(() => {
          setFavouriteLoading(false);
        });
    } else {
      updateAddCompanyToFavouriteInQueue({
        companyName: companyInfo.companyNameCPS,
        companyNameCPS: companyInfo.companyNameCPS,
        companyID: companyInfo.companyID,
        key: companyInfo.companyName,
        selected: true,
      });
      openLoginModal({
        isAddCompanyToFavouriteModal: true,
      });
    }
  };

  const getCalloutButtonContent = () => {
    let text = 'Follow Company';
    if (companyIsSaved) {
      text = 'Unfollow Company';
    }
    return <span>{text}</span>;
  };

  const getCalloutButtonIcon = () => (
    <div
      className={`z-font-18 ${
        companyIsSaved ? 'icon-icon-notification-off' : 'icon-icon-notification'
      }`}
    />
  );

  const getCallout = () => {
    const calloutButtonIcon = getCalloutButtonIcon();
    const calloutButtonContent = getCalloutButtonContent();

    return (
      <div className={subHeaderCalloutClasses['header-callout']}>
        <ZpButton
          theme="super-primary"
          icon={calloutButtonIcon}
          className={classNames(
            subHeaderCalloutClasses['header-callout-button'],
            subHeaderCalloutClasses['border-mobile'],
            subHeaderCalloutClasses['show-icon-desktop'],
            { [subHeaderCalloutClasses.inverse]: companyIsSaved }
          )}
          onClick={toggleSaveCompany}
          disabled={isFavouriteLoading}
          type="button"
        >
          {calloutButtonContent}
        </ZpButton>
      </div>
    );
  };

  return (
    <Fragment>
      <div className="company-search py-0">
        {claimedCompany && pendingChanges && (
          <div>
            <PendingChanges />
          </div>
        )}

        {!desktopDevice && (
          <SearchBar
            dynamicPage={dynamicPage}
            subPageName={subPageName}
            clusterCategory={CLUSTER_HEADER_CATEGORY.COMPANY}
            className="z-p-15 z-m-0"
            criteria={data.criteria}
          />
        )}

        {companyInfo?.companyCoverImage && (
          <div className="container z-px-0 z-pt-0 z-pt-md-20 z-px-md-15">
            <CoverImage url={companyInfo?.companyCoverImage} />
          </div>
        )}

        <div className="container">
          <div className="d-flex justify-content-center justify-content-md-between flex-column-reverse flex-lg-row align-items-start">
            <CompanyClusterHeaderTitle
              showClaimCompanyButton={
                canClaimCompany && !claimedCompany && !companyInfo?.companyCoverImage
              }
              subPageName={subPageName}
              subTitle={subTitle}
              title={getTitle(
                companyNameCPS || companyName,
                subPageName,
                subTitle || formatSubPageText(subPageName, CLUSTER_HEADER_CATEGORY.COMPANY),
                data
              )}
              data={data}
              dynamicPage={dynamicPage}
            />

            {desktopDevice && (
              <SearchBar
                dynamicPage={dynamicPage}
                subPageName={subPageName}
                clusterCategory={CLUSTER_HEADER_CATEGORY.COMPANY}
                className="z-mt-12 z-mt-md-20 mb-0"
                criteria={data.criteria}
              />
            )}
          </div>
        </div>
      </div>
      <div className="sub-header-container">
        <SubHeader callout={getCallout()} {...data} />
      </div>
      {/*mobile callout*/}
      {getCallout()}
    </Fragment>
  );
};

const mapStateToProps = ({ jobs, user, companyPage }) => ({
  userListingHashes: jobs,
  user,
  companyIsSaved: companyPage.companyIsSaved,
  favouriteCompanies: companyPage.userFavouriteCompanies,
});

const mapDispatchToProps = {
  openLoginModal,
  updateUserFavouriteCompanies,
  updateCompanyIsSaved,
  updateAddCompanyToFavouriteInQueue,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyClusterHeader);
